import React, {
  // useRef,
  useState,
  useEffect,
} from 'react';
import Box from '@mui/material/Box';
import DynamicCarouselSlide from './DynamicCarouselSlide';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import debug from 'debug';
import SplitPageTitle from 'components/Layout/SplitPageTitle';
import { CGProduct } from 'types/shopify';
const d = debug('DynamicCarousel');

export interface DynamicCarouselSlideOptions {
  ignoreTitles?: boolean;
  ignoreBackground?: boolean;
}

export interface DynamicCarouselSlideItem {
  title?: string;
  subtitle?: string;
  body?: React.ReactNode;
  bodyElement?: React.ReactNode;
  bodyImage?: string;
  caption?: string;
  backgroundImage?: string;
  buttonLabel?: string;
  buttonTo?: string;
  buttonUseClick?: boolean;
  options?: DynamicCarouselSlideOptions;
  product?: CGProduct;
  bright?: boolean;
}

interface DynamicCarouselContent {
  title?: string;
  subtitle?: string;
  backgroundImage?: string;
  slides?: DynamicCarouselSlideItem[];
}

interface DynamicCarouselProps {
  id?: string;
  displayMode?: 'default' | 'product';
  content?: DynamicCarouselContent;
  children?: any;
  smartMode?: boolean;
  infiniteLoop?: boolean;
  slidePreviewArrows?: boolean;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    carouselDefault: {
      '& .carousel .control-dots .dot': {
        boxShadow: 'none',
        outline: 'none',
      },
      '& .slider-wrapper': {
        '& .slide': {
          transform: 'translate3d(0px,0px,0px)',
        },
      },
    },
    carouselProduct: {
      '& .carousel .control-dots .dot': {
        boxShadow: 'none',
        outline: 'none',
      },
      '& .slider-wrapper': {
        padding: '0 calc(25% - 31px)',
        [theme.breakpoints.up(360)]: {
          padding: '0 calc(25% - 47px)',
        },
        [theme.breakpoints.up('sm')]: {
          padding: '0 calc(25% - 71px)',
        },
        [theme.breakpoints.up('md')]: {
          padding: '0 calc(25% - 100px)',
        },
      },
    },
  }),
  { name: 'DynamicCarousel' },
);

export const DC_DISPLAY_MODE_DEFAULT = 'default';
export const DC_DISPLAY_MODE_PRODUCT = 'product';

const DynamicCarousel: React.FC<DynamicCarouselProps> = ({ id, displayMode = 'default', content, children, smartMode = false, infiniteLoop = true, slidePreviewArrows = false }) => {
  const classes = useStyles();
  const [index, setIndex] = useState(0);

  const slideCount = content?.slides?.length || children?.length || 0;
  let defaultOptions = {
    autoPlay: true,
    centerMode: displayMode === DC_DISPLAY_MODE_PRODUCT ? true : false,
    className: displayMode === DC_DISPLAY_MODE_PRODUCT ? classes.carouselProduct : classes.carouselDefault,
    infiniteLoop: displayMode === DC_DISPLAY_MODE_PRODUCT ? false : true,
  };
  // const [item, setItem] = useState<DynamicCarouselSlideItem>(content?.slides?.[0] || {});
  const item = content?.slides?.[index];
  // Create portals for absolute positioned content...

  // Get the entire carousel background
  const carouselBackground = item?.backgroundImage || content?.backgroundImage;

  // Move to first slide after content (id) change...
  useEffect(() => {
    setIndex(0);
  }, [id, setIndex]);

  return (
    <Box
      sx={{
        position: 'relative',
        '& *': {
          // No text-selection pointer...
          userSelect: 'none',
        },
      }}
    >
      <Box
        sx={{
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundImage: carouselBackground ? `url(${carouselBackground})` : undefined,
          transition: 'all 0.5s ease-in-out',
          opacity: 0.5,
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
      />
      {displayMode === DC_DISPLAY_MODE_PRODUCT && (
        <Box
          sx={{ mt: 12 }}
          style={{
            userSelect: 'none',
            position: 'absolute',
            pointerEvents: 'none',
            width: '100%',
          }}
        >
          <SplitPageTitle title={item?.title || content?.title || ''} subtitle={item?.subtitle || content?.subtitle} />
        </Box>
      )}
      <Carousel
        className={defaultOptions.className}
        // carouselOptions
        showArrows={false}
        showThumbs={false}
        showStatus={false}
        preventMovementUntilSwipeScrollTolerance
        showIndicators={slideCount > 1}
        infiniteLoop={defaultOptions.infiniteLoop}
        autoPlay={defaultOptions.autoPlay}
        centerMode={defaultOptions.centerMode}
        centerSlidePercentage={100}
        interval={5000}
        swipeable={true}
        emulateTouch
        useKeyboardArrows
        selectedItem={index}
        onChange={(slideIndex: number) => {
          setIndex(slideIndex);
          d('jump', slideIndex);
        }}
      >
        {content?.slides?.map((slide, slideIdx) => (
          <DynamicCarouselSlide
            //
            key={slideIdx}
            item={slide}
            on={slideIdx === index}
          />
        )) || children}
      </Carousel>
    </Box>
  );
};

export default DynamicCarousel;
